/**
 * The registrar's own read-only Google Classroom grant for intake (ADR 0037,
 * decision 1). Connecting leaves this page for Google's consent screen; the
 * callback completes under the connection custody and returns here with
 * `?connection=connected` or `?connection=failed&reason=<code>`.
 */
import { useId } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  RegistrarPanel,
  RegistrarPanelBody,
} from "@/components/registrar/RegistrarWorkspacePrimitives";
import { Button } from "@/components/ui/button";
import {
  CLASSROOM_INTAKE_QUERY_KEY,
  classroomIntakeApi,
  classroomIntakeError,
  type ClassroomIntakeConnection,
} from "@/lib/api/classroomIntake";

function errorText(error: unknown, fallback: string): string {
  return classroomIntakeError(error)?.message || fallback;
}

function leaveForGoogle(url: string) {
  window.location.assign(url);
}

export function IntakeConnect({
  state,
  failure = null,
  leave = leaveForGoogle,
}: {
  state: "none" | "reconnect_required";
  /** Why the last return from Google did not connect, as a sentence. */
  failure?: string | null;
  /** Where the browser goes for consent; jsdom cannot navigate, so a test passes its own. */
  leave?: (url: string) => void;
}) {
  const id = useId();
  const start = useMutation({
    mutationFn: classroomIntakeApi.startConnection,
    onSuccess: ({ authorization_url }) => leave(authorization_url),
  });
  const reconnect = state === "reconnect_required";
  const action = reconnect ? "Reconnect Google Classroom" : "Connect Google Classroom";

  return (
    <RegistrarPanel weight="lead" aria-labelledby={`${id}-heading`}>
      <RegistrarPanelBody className="grid justify-items-start gap-3">
        <h3 id={`${id}-heading`} className="text-section font-semibold text-foreground">{action}</h3>
        <p className="max-w-2xl text-ui text-muted-foreground">
          {reconnect
            ? "Your Classroom access needs to be granted again. IMAM reads courses and rosters with read-only access through your own Google account, and nothing changes in Classroom."
            : "IMAM reads courses and rosters with read-only access through your own Google account. Nothing changes in Classroom."}
        </p>
        {failure && !start.isError && <p role="alert" className="max-w-2xl text-ui text-destructive">{failure}</p>}
        {start.isError && (
          <p role="alert" className="text-ui text-destructive">{errorText(start.error, "Google Classroom could not be reached. Try again.")}</p>
        )}
        <Button type="button" onClick={() => start.mutate()} disabled={start.isPending || start.isSuccess}>
          {start.isPending || start.isSuccess ? "Opening Google…" : action}
        </Button>
      </RegistrarPanelBody>
    </RegistrarPanel>
  );
}

export function IntakeConnectedAccount({ connection }: { connection: ClassroomIntakeConnection }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: classroomIntakeApi.revokeConnection,
    onSuccess: ({ google_grant }) => {
      toast.success("Google Classroom disconnected", {
        description: google_grant === "kept_for_teaching"
          ? "The teaching connection for that Google account stays active."
          : undefined,
      });
      void queryClient.invalidateQueries({ queryKey: CLASSROOM_INTAKE_QUERY_KEY });
    },
    onError: (error) => toast.error(errorText(error, "Google Classroom could not be disconnected")),
  });
  const account = connection.account;
  const who = account?.name && account.email
    ? `${account.name} (${account.email})`
    : account?.name || account?.email || null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-meta text-muted-foreground">
      <p className="min-w-0 [overflow-wrap:anywhere]">
        {who ? `Reading Classroom as ${who}, read-only.` : "Reading Classroom read-only."}
      </p>
      <Button type="button" size="sm" variant="ghost" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
        Disconnect
      </Button>
    </div>
  );
}
